import { firstRight } from 'ricos-content';
import { Node_Type } from 'ricos-schema';
import { markConverters, getUnsupportedMarkFromTiptap, getUnsupportedDecorationToTiptap } from './mark-converters.js';
import { appEmbedConverter } from './nodes/app-embed-converter.js';
import { audioConverter } from './nodes/audio-converter.js';
import { blockquoteConverter } from './nodes/blockquote-converter.js';
import { bulletedListConverter } from './nodes/bulleted-list-converter.js';
import { linkButtonConverter, actionButtonConverter } from './nodes/button-converters.js';
import { codeBlockConverter } from './nodes/code-block-converter.js';
import { collapsibleListConverter, collapsibleItemConverter, collapsibleItemTitleConverter, collapsibleItemBodyConverter } from './nodes/collapsible-list-converters.js';
import { dividerConverter } from './nodes/divider-converter.js';
import { embedConverter } from './nodes/embed-converter.js';
import { externalConverter } from './nodes/external-converter.js';
import { fileUploadConverter } from './nodes/file-upload-converter.js';
import { galleryConverter } from './nodes/gallery-converter.js';
import { gifConverter } from './nodes/gif-converter.js';
import { headingConverter } from './nodes/heading-converter.js';
import { htmlConverter } from './nodes/html-converter.js';
import { imageConverter } from './nodes/image-converter.js';
import { linkPreviewConverter } from './nodes/link-preview-converter.js';
import { listItemConverter } from './nodes/list-converters.js';
import { mapConverter } from './nodes/map-converter.js';
import { orderedListConverter } from './nodes/ordered-list-converter.js';
import { paragraphConverter } from './nodes/paragraph-converter.js';
import { pollConverter } from './nodes/poll-converter.js';
import { tableConverter, tableRowConverter, tableHeaderCellConverter } from './nodes/table-converters.js';
import { videoConverter } from './nodes/video-converter.js';

/**
 * Export.
 */

var toNoCase_1 = toNoCase;

/**
 * Test whether a string is camel-case.
 */

var hasSpace = /\s/;
var hasSeparator = /(_|-|\.|:)/;
var hasCamel = /([a-z][A-Z]|[A-Z][a-z])/;

/**
 * Remove any starting case from a `string`, like camel or snake, but keep
 * spaces and punctuation that may be important otherwise.
 *
 * @param {String} string
 * @return {String}
 */

function toNoCase(string) {
  if (hasSpace.test(string)) return string.toLowerCase()
  if (hasSeparator.test(string)) return (unseparate(string) || string).toLowerCase()
  if (hasCamel.test(string)) return uncamelize(string).toLowerCase()
  return string.toLowerCase()
}

/**
 * Separator splitter.
 */

var separatorSplitter = /[\W_]+(.|$)/g;

/**
 * Un-separate a `string`.
 *
 * @param {String} string
 * @return {String}
 */

function unseparate(string) {
  return string.replace(separatorSplitter, function (m, next) {
    return next ? ' ' + next : ''
  })
}

/**
 * Camelcase splitter.
 */

var camelSplitter = /(.)([A-Z]+)/g;

/**
 * Un-camelcase a `string`.
 *
 * @param {String} string
 * @return {String}
 */

function uncamelize(string) {
  return string.replace(camelSplitter, function (m, previous, uppers) {
    return previous + ' ' + uppers.toLowerCase().split('').join(' ')
  })
}

var clean = toNoCase_1;

/**
 * Export.
 */

var toSpaceCase_1 = toSpaceCase;

/**
 * Convert a `string` to space case.
 *
 * @param {String} string
 * @return {String}
 */

function toSpaceCase(string) {
  return clean(string).replace(/[\W_]+(.|$)/g, function (matches, match) {
    return match ? ' ' + match : ''
  }).trim()
}

var space = toSpaceCase_1;

/**
 * Export.
 */

var toCamelCase_1 = toCamelCase;

/**
 * Convert a `string` to camel case.
 *
 * @param {String} string
 * @return {String}
 */

function toCamelCase(string) {
  return space(string).replace(/\s(\w)/g, function (matches, letter) {
    return letter.toUpperCase()
  })
}

const textConverter = {
  toTiptap: {
    types: [Node_Type.TEXT],
    convert: (node) => {
      if (node.textData.text === "\n") {
        return {
          type: "hardBreak",
          text: "\n",
          marks: [],
          attrs: {
            id: ""
          }
        };
      }
      return {
        type: "text",
        text: node.textData.text,
        marks: decorationsToMarks(node.textData.decorations),
        attrs: {
          id: ""
        }
      };
    }
  },
  fromTiptap: {
    types: ["hardBreak", "text"],
    convert: (node) => {
      if (node.type === "hardBreak") {
        return {
          type: Node_Type.TEXT,
          id: "",
          nodes: [],
          textData: {
            text: "\n",
            decorations: []
          }
        };
      }
      return {
        type: Node_Type.TEXT,
        id: "",
        nodes: [],
        textData: {
          text: node.text || "",
          decorations: marksToDecorations(node.marks || [])
        }
      };
    }
  }
};

var __defProp$1 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$1.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$1.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const getUnsupportedToTiptap = (node) => {
  const dataProp = Object.keys(node).find((p) => p.endsWith(`${toCamelCase_1(node.type)}Data`));
  return {
    types: [node.type],
    convert: (node2, visit) => {
      const content = visit(node2);
      return __spreadValues$1({
        type: node2.type,
        attrs: __spreadProps$1(__spreadValues$1({}, dataProp ? node2[dataProp] : {}), {
          id: node2.id
        })
      }, content.length > 0 ? { content } : {});
    }
  };
};
const getUnsupportedFromTiptap = (node) => {
  const _a = node.attrs || {}, { id } = _a, data = __objRest(_a, ["id"]);
  return {
    types: [node.type],
    convert: (node2, visit) => ({
      type: node2.type,
      id,
      [`${toCamelCase_1(node2.type)}Data`]: __spreadValues$1({}, data),
      nodes: visit(node2)
    })
  };
};
const nodeConverters = [
  imageConverter,
  dividerConverter,
  textConverter,
  paragraphConverter,
  blockquoteConverter,
  headingConverter,
  linkButtonConverter,
  actionButtonConverter,
  listItemConverter,
  collapsibleListConverter,
  collapsibleItemConverter,
  collapsibleItemTitleConverter,
  collapsibleItemBodyConverter,
  tableConverter,
  tableRowConverter,
  tableHeaderCellConverter,
  codeBlockConverter,
  audioConverter,
  embedConverter,
  fileUploadConverter,
  galleryConverter,
  gifConverter,
  mapConverter,
  videoConverter,
  pollConverter,
  htmlConverter,
  linkPreviewConverter,
  orderedListConverter,
  bulletedListConverter,
  appEmbedConverter,
  externalConverter
];

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const convertTimestamp = (timestamp) => firstRight(timestamp, "", [
  [(t) => typeof t === "string", (t) => t],
  [(t) => typeof (t == null ? void 0 : t.toISOString) === "function", (t) => t.toISOString()]
]);
const convertMetadata = (metadata) => __spreadProps(__spreadValues({}, metadata), {
  createdTimestamp: convertTimestamp(metadata.createdTimestamp),
  updatedTimestamp: convertTimestamp(metadata.updatedTimestamp)
});
const normalizeTimestamps = (metadata) => {
  const { createdTimestamp, updatedTimestamp } = metadata != null ? metadata : {};
  return __spreadValues(__spreadValues({
    version: metadata == null ? void 0 : metadata.version,
    id: metadata == null ? void 0 : metadata.id
  }, createdTimestamp ? { createdTimestamp } : {}), updatedTimestamp ? { updatedTimestamp } : {});
};
const FROM_TIPTAP_BY_TYPE = Object.fromEntries(
  [...nodeConverters].reverse().flatMap((converter) => converter.fromTiptap.types.map((type) => [type, converter.fromTiptap]))
);
const getFromTiptapConverter = (node) => FROM_TIPTAP_BY_TYPE[node.type] || getUnsupportedFromTiptap(node);
const fromTiptapNode = (node) => getFromTiptapConverter(node).convert(node, fromTipTapToNodes);
function fromTipTapToNodes(node) {
  return (node.content || []).map(fromTiptapNode);
}
const fromTiptap = (content) => {
  var _a, _b;
  return {
    documentStyle: (_a = content.attrs) == null ? void 0 : _a.documentStyle,
    metadata: normalizeTimestamps((_b = content.attrs) == null ? void 0 : _b.metadata),
    nodes: fromTipTapToNodes(content)
  };
};
const tiptapNodeVisitor = fromTipTapToNodes;
const TO_TIPTAP_BY_TYPE = Object.fromEntries(
  [...nodeConverters].reverse().flatMap((converter) => converter.toTiptap.types.map((type) => [type, converter.toTiptap]))
);
const getToTiptapConverter = (node) => TO_TIPTAP_BY_TYPE[node.type] || getUnsupportedToTiptap(node);
const toTiptapNode = (node) => getToTiptapConverter(node).convert(node, fromRicosToTiptapNodes);
function fromRicosToTiptapNodes(node) {
  return (node.nodes || []).map(toTiptapNode);
}
const toTiptapNodeAttrs = (node) => toTiptapNode(node).attrs;
const toTiptap = (content) => {
  const ricosRoot = { id: "root", type: Node_Type.UNRECOGNIZED, nodes: content.nodes };
  return {
    type: "doc",
    content: fromRicosToTiptapNodes(ricosRoot),
    attrs: {
      metadata: convertMetadata(content.metadata || { version: 1 }),
      documentStyle: content.documentStyle || {}
    }
  };
};
const ricosNodeVisitor = fromRicosToTiptapNodes;
const DECORATIONS_TO_MARKS_BY_TYPE = Object.fromEntries(
  [...markConverters].reverse().flatMap((converter) => converter.toTiptap.types.map((type) => [type, converter.toTiptap]))
);
const getDecorationToMarkConverter = (decoration) => DECORATIONS_TO_MARKS_BY_TYPE[decoration.type] || getUnsupportedDecorationToTiptap(decoration);
const decorationsToMarks = (decorations) => decorations.flatMap(
  (decoration) => getDecorationToMarkConverter(decoration).convert(decoration)
);
const MARKS_TO_DECORATIONS_BY_TYPE = Object.fromEntries(
  [...markConverters].reverse().flatMap((converter) => converter.fromTiptap.types.map((type) => [type, converter.fromTiptap]))
);
const marksToDecorations = (marks) => {
  if (!marks.length) {
    return [];
  }
  const conversions = /* @__PURE__ */ new Map();
  for (const mark of marks) {
    const converter = MARKS_TO_DECORATIONS_BY_TYPE[mark.type] || getUnsupportedMarkFromTiptap(mark);
    conversions.set(converter, [...conversions.get(converter) || [], mark]);
  }
  return Array.from(conversions.entries()).flatMap(
    ([converter, cMarks]) => converter.convert(cMarks)
  );
};

export { fromTiptap as a, toCamelCase_1 as b, toTiptap as c, textConverter as d, getUnsupportedFromTiptap as e, fromTiptapNode as f, getUnsupportedToTiptap as g, fromTipTapToNodes as h, tiptapNodeVisitor as i, toTiptapNode as j, fromRicosToTiptapNodes as k, decorationsToMarks as l, marksToDecorations as m, nodeConverters as n, ricosNodeVisitor as r, toTiptapNodeAttrs as t };
//# sourceMappingURL=tiptap-converters-42e0528e.js.map
